import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { ChannelLogo } from '../components/channel-logo';
import { ChevronIcon, CheckIcon, SearchIcon, XIcon } from '../components/icons';
import {
  useChannelSearchQuery,
  useChannelsQuery,
  useExploreDataQuery,
  useFollowChannelMutation,
  useSubscribedChannelsQuery,
  useUnfollowChannelMutation,
} from '../hooks/use-channels-query';
import { useFeedSearch } from '../hooks/use-feed-query';
import { useGuideSearchQuery } from '../hooks/use-guides-query';
import { useProfileQuery } from '../hooks/use-profile-query';
import {
  useSubscribeTopicMutation,
  useTopicSubscriptionsQuery,
  useTopicsQuery,
  useUnsubscribeTopicMutation,
} from '../hooks/use-topics-query';
import { getCountryName, getFlag } from '../config/reference-data';
import type { Channel, FeedArticle } from '../types';
import type { ExpatGuideTopic } from '../types/expat-guides';

type SearchTab = 'channels' | 'articles' | 'guides';

// Mobile-aligned Explore: single scrollable column with search at top,
// trending topics rail, curated channel sections (Popular / Local / Home /
// World) each capped at 5 entries, and a final "All channels" preview.
// When the search input has 2+ chars, the rest of the page swaps for the
// search results panel with three tabs (Channels / Articles / Guides).
export function Explore() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [searchTab, setSearchTab] = useState<SearchTab>('channels');

  const profile = useProfileQuery();
  const explore = useExploreDataQuery();
  const allChannels = useChannelsQuery();
  const subscribed = useSubscribedChannelsQuery();
  const topicsQuery = useTopicsQuery();
  const topicSubs = useTopicSubscriptionsQuery();
  const subTopic = useSubscribeTopicMutation();
  const unsubTopic = useUnsubscribeTopicMutation();

  const channelSearch = useChannelSearchQuery(search);
  const articleSearch = useFeedSearch(search);
  const guideSearch = useGuideSearchQuery(search);

  const followingSet = useMemo(
    () => new Set((subscribed.data ?? []).map((c) => c.id)),
    [subscribed.data],
  );
  const followedTopicSet = useMemo(
    () => new Set((topicSubs.data ?? []).map((t) => t.topicSlug)),
    [topicSubs.data],
  );

  const trimmedSearch = search.trim();
  const isSearching = trimmedSearch.length >= 2;

  const currentCountryCode = profile.data?.currentCountry ?? null;
  const homeCountryCode = profile.data?.homeCountry ?? null;
  const currentCountryName = getCountryName(currentCountryCode);
  const homeCountryName = getCountryName(homeCountryCode);
  const showHomeSection = homeCountryCode !== null && homeCountryCode !== currentCountryCode;

  const popularSection = (explore.data?.popular ?? []).slice(0, 5);
  const localSection = (explore.data?.localNews ?? []).slice(0, 5);
  const homeSection = showHomeSection ? (explore.data?.homeCountryNews ?? []).slice(0, 5) : [];
  const worldSection = (explore.data?.worldNews ?? []).slice(0, 5);

  return (
    <div className="nw-explore">
      <div className="nw-explore__hero">
        <h1 className="nw-explore__title">{t('web.explore.title')}</h1>
        <p className="nw-explore__sub">{t('web.explore.subtitle')}</p>
      </div>

      <div className="nw-explore__search">
        <SearchIcon size={16} stroke={2} />
        <input
          type="search"
          placeholder={t('web.explore.search.placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t('web.explore.search.ariaLabel')}
        />
        {search && (
          <button
            type="button"
            className="nw-search__clear"
            onClick={() => setSearch('')}
            aria-label={t('web.explore.search.clearAriaLabel')}
          >
            <XIcon size={12} stroke={2.4} />
          </button>
        )}
      </div>

      {isSearching ? (
        <SearchPanel
          t={t}
          query={trimmedSearch}
          tab={searchTab}
          onTab={setSearchTab}
          channels={channelSearch.data ?? []}
          articles={articleSearch.data ?? []}
          guides={guideSearch.data ?? []}
          loading={channelSearch.isLoading || articleSearch.isLoading || guideSearch.isLoading}
          followingSet={followingSet}
        />
      ) : (
        <>
          <TrendingTopics
            t={t}
            topics={topicsQuery.data ?? []}
            loading={topicsQuery.isLoading}
            followedTopicSet={followedTopicSet}
            onTopicClick={(slug) => navigate(`/tag/${encodeURIComponent(slug)}`)}
            onTopicToggle={(slug) => {
              if (subTopic.isPending || unsubTopic.isPending) return;
              if (followedTopicSet.has(slug)) unsubTopic.mutate(slug);
              else subTopic.mutate(slug);
            }}
          />

          {explore.isLoading && (
            <div className="nw-feed__loading">
              <div className="nw-feed__loading-spinner" aria-hidden />
              {t('web.explore.loading')}
            </div>
          )}
          {explore.isError && (
            <div className="nw-feed__empty">{t('web.explore.error')}</div>
          )}

          {!explore.isLoading && !explore.isError && (
            <>
              {popularSection.length > 0 && (
                <ChannelRail
                  eyebrow={t('web.explore.sections.popular.eyebrow')}
                  title={t('web.explore.sections.popular.title')}
                  subtitle={t('web.explore.sections.popular.subtitle')}
                  channels={popularSection}
                  followingSet={followingSet}
                />
              )}

              {localSection.length > 0 && (
                <ChannelRail
                  eyebrow={t('web.explore.sections.local.eyebrow')}
                  title={
                    currentCountryName
                      ? t('web.explore.sections.local.title', { country: currentCountryName })
                      : t('web.explore.sections.local.titleFallback')
                  }
                  subtitle={t('web.explore.sections.local.subtitle')}
                  flag={getFlag(currentCountryCode)}
                  channels={localSection}
                  followingSet={followingSet}
                />
              )}

              {homeSection.length > 0 && (
                <ChannelRail
                  eyebrow={t('web.explore.sections.home.eyebrow')}
                  title={
                    homeCountryName
                      ? t('web.explore.sections.home.title', { country: homeCountryName })
                      : t('web.explore.sections.home.titleFallback')
                  }
                  subtitle={t('web.explore.sections.home.subtitle')}
                  flag={getFlag(homeCountryCode)}
                  channels={homeSection}
                  followingSet={followingSet}
                />
              )}

              {worldSection.length > 0 && (
                <ChannelRail
                  eyebrow={t('web.explore.sections.world.eyebrow')}
                  title={t('web.explore.sections.world.title')}
                  subtitle={t('web.explore.sections.world.subtitle')}
                  channels={worldSection}
                  followingSet={followingSet}
                />
              )}

              {(allChannels.data ?? []).length > 0 && (
                <ChannelRail
                  eyebrow={t('web.explore.sections.all.eyebrow')}
                  title={t('web.explore.sections.all.title')}
                  subtitle={t('web.explore.sections.all.subtitle', { count: allChannels.data!.length })}
                  channels={(allChannels.data ?? []).slice(0, 8)}
                  followingSet={followingSet}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

interface ChannelRailProps {
  readonly eyebrow: string;
  readonly title: string;
  readonly subtitle?: string;
  readonly flag?: string;
  readonly channels: readonly Channel[];
  readonly followingSet: ReadonlySet<string>;
}

function ChannelRail({ eyebrow, title, subtitle, flag, channels, followingSet }: ChannelRailProps) {
  const navigate = useNavigate();
  return (
    <div className="nw-rail">
      <div className="nw-rail__head">
        <div>
          <div className="nw-section-eyebrow">{eyebrow}</div>
          <div className="nw-rail__title-row">
            {flag && <span className="nw-rail__flag" aria-hidden>{flag}</span>}
            <h2 className="nw-rail__title">{title}</h2>
          </div>
          {subtitle && <p className="nw-rail__sub">{subtitle}</p>}
        </div>
      </div>
      <div className="nw-rail__list">
        {channels.map((c) => (
          <ChannelRow
            key={c.id}
            channel={c}
            isFollowing={followingSet.has(c.id)}
            onOpen={() => navigate(`/channel/${encodeURIComponent(c.id)}`)}
          />
        ))}
      </div>
    </div>
  );
}

interface ChannelRowProps {
  readonly channel: Channel;
  readonly isFollowing: boolean;
  readonly onOpen: () => void;
}

function ChannelRow({ channel, isFollowing, onOpen }: ChannelRowProps) {
  const { t } = useTranslation();
  const follow = useFollowChannelMutation();
  const unfollow = useUnfollowChannelMutation();
  const pending = follow.isPending || unfollow.isPending;
  return (
    <button
      type="button"
      className="nw-explore-row"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('[data-follow]')) return;
        onOpen();
      }}
    >
      <ChannelLogo name={channel.name} logoUrl={channel.logoUrl} size={44} />
      <div className="nw-explore-row__body">
        <div className="nw-explore-row__name">{channel.name}</div>
        {channel.description && <div className="nw-explore-row__desc">{channel.description}</div>}
        <div className="nw-explore-row__meta">
          {channel.language?.toUpperCase()}
          {channel.country ? ` · ${getCountryName(channel.country) ?? channel.country}` : ''}
        </div>
      </div>
      <span
        data-follow
        role="button"
        tabIndex={0}
        className={`nw-channel-row__follow ${isFollowing ? 'is-following' : ''}`}
        aria-disabled={pending}
        onClick={(e) => {
          e.stopPropagation();
          if (pending) return;
          if (isFollowing) unfollow.mutate(channel.id);
          else follow.mutate(channel.id);
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          e.stopPropagation();
          if (pending) return;
          if (isFollowing) unfollow.mutate(channel.id);
          else follow.mutate(channel.id);
        }}
      >
        {isFollowing ? t('web.explore.follow.following') : t('web.explore.follow.follow')}
      </span>
      <ChevronIcon dir="right" size={16} stroke={2} />
    </button>
  );
}

interface TrendingProps {
  readonly t: TFunction;
  readonly topics: ReturnType<typeof useTopicsQuery>['data'];
  readonly loading: boolean;
  readonly followedTopicSet: ReadonlySet<string>;
  readonly onTopicClick: (slug: string) => void;
  readonly onTopicToggle: (slug: string) => void;
}

function TrendingTopics({ t, topics, loading, followedTopicSet, onTopicClick, onTopicToggle }: TrendingProps) {
  if (loading) {
    return (
      <div className="nw-trending-block">
        <div className="nw-section-eyebrow">{t('web.explore.trending.eyebrow')}</div>
        <div className="nw-section-title-md">{t('web.explore.trending.title')}</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('web.explore.trending.loading')}</div>
      </div>
    );
  }
  if (!topics || topics.length === 0) return null;
  return (
    <div className="nw-trending-block">
      <div>
        <div className="nw-section-eyebrow">{t('web.explore.trending.eyebrow')}</div>
        <div className="nw-section-title-md">{t('web.explore.trending.title')}</div>
      </div>
      <div className="nw-topic-rail">
        {topics.slice(0, 14).map((topic) => {
          const on = followedTopicSet.has(topic.slug);
          return (
            <button
              key={topic.slug}
              type="button"
              className={`nw-topic-chip ${on ? 'is-on' : ''}`}
              onClick={() => onTopicClick(topic.slug)}
              onContextMenu={(e) => {
                // Right-click toggles follow — mirrors mobile's long-press.
                e.preventDefault();
                onTopicToggle(topic.slug);
              }}
              title={
                on
                  ? t('web.explore.trending.topicTitle_following', { label: topic.label })
                  : t('web.explore.trending.topicTitle_notFollowing', { label: topic.label })
              }
              style={{ whiteSpace: 'nowrap' }}
            >
              {on && <span style={{ marginRight: 4 }}>★</span>}
              {topic.label}
            </button>
          );
        })}
      </div>
      {followedTopicSet.size > 0 && (
        <div className="nw-following-banner">
          <span className="nw-following-banner__check" aria-hidden>
            <CheckIcon size={12} stroke={3} />
          </span>
          <span>
            <Trans
              i18nKey="web.explore.trending.followingBanner"
              count={followedTopicSet.size}
              values={{ count: followedTopicSet.size }}
              components={{ strong: <strong style={{ color: 'var(--accent)' }} /> }}
            />
          </span>
        </div>
      )}
    </div>
  );
}

interface SearchPanelProps {
  readonly t: TFunction;
  readonly query: string;
  readonly tab: SearchTab;
  readonly onTab: (t: SearchTab) => void;
  readonly channels: readonly Channel[];
  readonly articles: readonly FeedArticle[];
  readonly guides: readonly ExpatGuideTopic[];
  readonly loading: boolean;
  readonly followingSet: ReadonlySet<string>;
}

function SearchPanel({ t, query, tab, onTab, channels, articles, guides, loading, followingSet }: SearchPanelProps) {
  const navigate = useNavigate();
  const counts: Record<SearchTab, number> = {
    channels: channels.length,
    articles: articles.length,
    guides: guides.length,
  };
  const empty = !loading && counts[tab] === 0;
  const tabLabels: Record<SearchTab, string> = {
    channels: t('web.explore.search.tabs.channels'),
    articles: t('web.explore.search.tabs.articles'),
    guides: t('web.explore.search.tabs.guides'),
  };
  const emptyMessage: Record<SearchTab, string> = {
    channels: t('web.explore.search.emptyChannels', { query }),
    articles: t('web.explore.search.emptyArticles', { query }),
    guides: t('web.explore.search.emptyGuides', { query }),
  };

  return (
    <>
      <div className="nw-explore__tabs">
        {(['channels', 'articles', 'guides'] as const).map((tabKey) => (
          <button
            key={tabKey}
            type="button"
            className={`nw-explore__tab ${tab === tabKey ? 'is-active' : ''}`}
            onClick={() => onTab(tabKey)}
          >
            {tabLabels[tabKey]} {counts[tabKey] > 0 && <span style={{ opacity: 0.7 }}>{counts[tabKey]}</span>}
          </button>
        ))}
      </div>

      {loading && (
        <div className="nw-feed__loading">
          <div className="nw-feed__loading-spinner" aria-hidden />
          {t('web.explore.search.loadingFor', { query })}
        </div>
      )}

      {empty && <div className="nw-feed__empty">{emptyMessage[tab]}</div>}

      {tab === 'channels' && channels.length > 0 && (
        <div className="nw-rail">
          <div className="nw-rail__head">
            <div>
              <div className="nw-section-eyebrow">{t('web.explore.sections.channelsHeader.eyebrow')}</div>
              <h2 className="nw-rail__title">{t('web.explore.search.matches', { count: channels.length })}</h2>
            </div>
          </div>
          <div className="nw-rail__list">
            {channels.map((c) => (
              <ChannelRow
                key={c.id}
                channel={c}
                isFollowing={followingSet.has(c.id)}
                onOpen={() => navigate(`/channel/${encodeURIComponent(c.id)}`)}
              />
            ))}
          </div>
        </div>
      )}

      {tab === 'articles' && articles.length > 0 && (
        <div className="nw-rail">
          <div className="nw-rail__head">
            <div>
              <div className="nw-section-eyebrow">{t('web.explore.sections.articlesHeader.eyebrow')}</div>
              <h2 className="nw-rail__title">{t('web.explore.search.matches', { count: articles.length })}</h2>
            </div>
          </div>
          <div className="nw-rail__list">
            {articles.map((a) => (
              <button
                key={a.id}
                type="button"
                className="nw-explore-row"
                onClick={() => navigate(`/article/${encodeURIComponent(a.id)}`)}
              >
                <ChannelLogo name={a.channelName} logoUrl={a.channelLogo} size={44} />
                <div className="nw-explore-row__body">
                  <div className="nw-explore-row__name">{a.title}</div>
                  <div className="nw-explore-row__meta">{a.channelName}</div>
                </div>
                <ChevronIcon dir="right" size={16} stroke={2} />
              </button>
            ))}
          </div>
        </div>
      )}

      {tab === 'guides' && guides.length > 0 && (
        <div className="nw-rail">
          <div className="nw-rail__head">
            <div>
              <div className="nw-section-eyebrow">{t('web.explore.sections.guidesHeader.eyebrow')}</div>
              <h2 className="nw-rail__title">{t('web.explore.search.matches', { count: guides.length })}</h2>
            </div>
          </div>
          <div className="nw-rail__list">
            {guides.map((g) => (
              <button
                key={g.id}
                type="button"
                className="nw-explore-row"
                onClick={() => navigate(`/guides/${g.id}`)}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 9,
                    background: 'color-mix(in oklab, var(--accent), transparent 86%)',
                    color: 'var(--accent)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'var(--font-display)',
                    fontWeight: 800,
                    fontSize: 18,
                  }}
                  aria-hidden
                >
                  ▤
                </div>
                <div className="nw-explore-row__body">
                  <div className="nw-explore-row__name">{g.content.title}</div>
                  <div className="nw-explore-row__desc">{g.content.subtitle}</div>
                  <div className="nw-explore-row__meta">{g.category}</div>
                </div>
                <ChevronIcon dir="right" size={16} stroke={2} />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
